/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { forwardRef, Ref, useEffect, useRef, useState } from 'react';
import { classNames } from 'primereact/utils';

import { WorkflowFactory } from 'cwlts/models';
import { Workflow as WorkflowModel } from 'cwlts/mappings/v1.0';
import {
  SelectionPlugin,
  SVGArrangePlugin,
  SVGEdgeHoverPlugin,
  SVGNodeMovePlugin,
  Workflow,
  ZoomPlugin
} from 'cwl-svg';

import { Message } from 'primereact/message';
import { Skeleton } from 'primereact/skeleton';

export interface CWLViewerProps {
  className?: string;
  outerRefresh?: string;
  workflow?: WorkflowModel | null;
  loading?: boolean;
}

export const CWLViewer = forwardRef<HTMLDivElement, CWLViewerProps>(({ className, workflow, ...props }, ref) => {
  const [error, setError] = useState<string>();
  const svgRef = useRef<SVGSVGElement>(null);

  const loadViewer = async (pane: SVGSVGElement, cwl: WorkflowModel) => {
    if (!cwl) {
      setError('cwl cannot be found or is badly formatted');
      return;
    }
    const wf = WorkflowFactory.from(cwl);
    const workflow = new Workflow({
      model: wf,
      svgRoot: pane,
      editingEnabled: true,
      plugins: [
        new SVGArrangePlugin(),
        new SVGEdgeHoverPlugin(),
        new SVGNodeMovePlugin({
          movementSpeed: 3
        }),
        new SelectionPlugin(),
        new ZoomPlugin()
      ]
    });
    const arranger = workflow.getPlugin(SVGArrangePlugin);
    if (arranger) {
      arranger.arrange();
    }
  };

  useEffect(() => {
    if (svgRef.current && workflow) {
      loadViewer(svgRef.current, workflow);
    }
    const refreshEvent = () => {
      if (svgRef.current && workflow) {
        loadViewer(svgRef.current, workflow);
      }
    };

    const outerRefresh = window.document.getElementById(props.outerRefresh ?? '');
    if (outerRefresh) {
      outerRefresh.addEventListener('click', refreshEvent);
    }

    const scrollDisabling = () => {
      document.body.style.overflow = 'hidden';
    };
    const scrollEnabling = () => {
      document.body.style.overflow = 'auto';
    };

    svgRef.current?.addEventListener('mouseover', scrollDisabling);

    svgRef.current?.addEventListener('mouseleave', scrollEnabling);

    return () => {
      if (outerRefresh) {
        outerRefresh.removeEventListener('click', refreshEvent);
      }
      if (svgRef.current) {
        svgRef.current.removeEventListener('mouseover', scrollDisabling);
        svgRef.current.removeEventListener('mouseleave', scrollEnabling);
      }
    };
  }, [props.outerRefresh, props.loading, workflow]);

  return (
    <div ref={ref as Ref<HTMLDivElement>} className={classNames(className, 'w-full h-full')}>
      {props.loading ? (
        <Skeleton height="20rem" />
      ) : error ? (
        <Message className="w-full" severity="error" text={error} />
      ) : (
        <div className="h-full w-full">
          <svg className="w-full h-full cwl-workflow" ref={svgRef}></svg>
        </div>
      )}
    </div>
  );
});
